import React, { useEffect, useState } from 'react';
import { useMoodStore } from '../lib/store';
import { LineChart, Brain, Plus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface MoodLog {
  id: string;
  mood_rating: number;
  notes: string;
  created_at: string;
}

function MoodTracker() {
  const { moodLogs, loadMoodLogs, addMoodLog } = useMoodStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [moodRating, setMoodRating] = useState(3);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMoodLogs = async () => {
      try {
        await loadMoodLogs();
      } catch (err: any) {
        setError(err.message || 'Failed to load mood logs');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMoodLogs();
  }, [loadMoodLogs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      await addMoodLog({
        mood_rating: moodRating,
        notes,
      });
      setIsModalOpen(false);
      setMoodRating(3);
      setNotes('');
    } catch (err: any) {
      setError(err.message || 'Failed to save mood log');
    } finally {
      setIsSaving(false);
    }
  };

  const getMoodEmoji = (rating: number) => {
    switch (rating) {
      case 1: return '😢';
      case 2: return '😕';
      case 3: return '😐';
      case 4: return '🙂';
      case 5: return '😄';
      default: return '😐';
    }
  };

  const getMoodLabel = (rating: number) => {
    switch (rating) {
      case 1: return 'Very Bad';
      case 2: return 'Bad';
      case 3: return 'Okay';
      case 4: return 'Good';
      case 5: return 'Very Good';
      default: return 'Okay';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading mood logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Brain className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Mood Tracker</h1>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Log Mood
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Mood History */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {moodLogs.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No mood logs yet</h3>
            <p className="text-gray-500">Start tracking your mood by clicking the "Log Mood" button.</p>
          </div>
        ) : (
          moodLogs.map((log: MoodLog) => (
            <div key={log.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-4xl">{getMoodEmoji(log.mood_rating)}</div>
                <div className="text-gray-500">
                  {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-800">
                  {getMoodLabel(log.mood_rating)}
                </p>
                {log.notes && (
                  <p className="text-gray-600">{log.notes}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Mood Log Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">How are you feeling?</h2>
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mood Rating
                </label>
                <div className="flex justify-between items-center">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setMoodRating(rating)}
                      disabled={isSaving}
                      className={`text-4xl p-2 rounded-full transition-transform ${
                        moodRating === rating
                          ? 'transform scale-125 bg-blue-100'
                          : 'hover:transform hover:scale-110'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {getMoodEmoji(rating)}
                    </button>
                  ))}
                </div>
                <div className="text-center mt-2 text-gray-600">
                  {getMoodLabel(moodRating)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isSaving}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                  placeholder="How are you feeling today?"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setMoodRating(3);
                    setNotes('');
                    setError('');
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MoodTracker;